import { apiGet, apiPatch } from '../../utils/api';
import { config } from '../../config/app-config';
import { showErrorNotification, showSuccessNotification } from '../../utils/notifications';

/**
 * Opens a modal to link a page to a block.
 * @param {string} blockId - the block's DB ID
 * @param {string|null} currentSlug - currently linked page slug, or null
 * @param {function} onLinked - callback(service) called after successful link
 * @param {object} options - { listPath, linkPath } — defaults to diagnostics endpoints
 */
export async function openLinkPageModal(blockId, currentSlug, onLinked, options = {}) {
    const listPath = options.listPath || config.api.paths.diagnostics;
    const linkPath = options.linkPath || config.api.paths.diagnosticsLinkBlock;

    let pages = [];
    try {
        pages = await apiGet(listPath, { auth: false });
    } catch {
        showErrorNotification('Не вдалося завантажити список сторінок');
        return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'edit-modal';

    const linked = pages.find(p => p.slug === currentSlug) || null;
    const available = pages.filter(p => !linked || p.id !== linked.id);

    const listItems = [
        linked ? `<li class="page-list-item page-list-item--current" data-id="${linked.id}" data-slug="${linked.slug}">
            <span class="page-status page-status--${linked.status}"></span>
            ${linked.title} <em style="opacity:.5;font-size:11px;">(поточна)</em>
        </li>` : '',
        ...available.map(p => `<li class="page-list-item" data-id="${p.id}" data-slug="${p.slug}">
                <span class="page-status page-status--${p.status}"></span>
                ${p.title}${p.block_id ? ' <em style="opacity:.4;font-size:11px;">(вже прив\'язана)</em>' : ''}
            </li>`)
    ].join('');

    const pendingMode = !blockId;
    const description = pendingMode
        ? 'Блок ще не опублікований. Прив\'язку буде застосовано автоматично після публікації змін.'
        : 'Оберіть сторінку зі списку. Якщо потрібна сторінка відсутня — спочатку створіть її.';

    overlay.innerHTML = `
        <div class="modal-content" style="max-width:480px;">
            <h3 style="margin:0 0 12px;font-size:16px;font-weight:600;">Прив'язати сторінку до картки</h3>
            <p style="margin:0 0 12px;font-size:13px;opacity:.6;${pendingMode ? 'color:#ff9800;' : ''}">${description}</p>
            <ul id="page-list" style="list-style:none;padding:0;margin:0 0 16px;max-height:280px;overflow-y:auto;border:1px solid #eee;border-radius:8px;">
                ${listItems || '<li style="padding:12px;opacity:.5;text-align:center;">Немає доступних сторінок</li>'}
            </ul>
            <div class="buttons">
                ${linked ? `<button class="unlink-btn" style="margin-right:auto;padding:8px 14px;border:1px solid #ccc;border-radius:8px;background:transparent;color:#d32f2f;cursor:pointer;">Відв\'язати</button>` : ''}
                <button class="cancel-btn">Скасувати</button>
                <button class="save-btn" disabled>Прив'язати</button>
            </div>
        </div>
    `;

    document.body.appendChild(overlay);

    const styleTag = document.createElement('style');
    styleTag.textContent = `
        .page-list-item {
            padding: 10px 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            border-bottom: 1px solid #f0f0f0;
            font-size: 14px;
            transition: background .15s;
        }
        .page-list-item:last-child { border-bottom: none; }
        .page-list-item:hover { background: #f5f7ff; }
        .page-list-item--selected { background: #e8eeff; font-weight: 500; }
        .page-list-item--current { background: #fff8e1; }
        .page-status {
            width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        }
        .page-status--published { background: #4caf50; }
        .page-status--draft { background: #bbb; }
    `;
    document.head.appendChild(styleTag);

    const list = overlay.querySelector('#page-list');
    const saveBtn = overlay.querySelector('.save-btn');
    const cancelBtn = overlay.querySelector('.cancel-btn');
    const unlinkBtn = overlay.querySelector('.unlink-btn');
    let selectedId = null;
    let selectedSlug = null;

    list.addEventListener('click', (e) => {
        const item = e.target.closest('.page-list-item');
        if (!item) return;
        list.querySelectorAll('.page-list-item').forEach(i => i.classList.remove('page-list-item--selected'));
        item.classList.add('page-list-item--selected');
        selectedId = item.dataset.id;
        selectedSlug = item.dataset.slug;
        saveBtn.disabled = false;
    });

    function close() {
        overlay.remove();
        styleTag.remove();
    }

    cancelBtn.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    if (unlinkBtn) {
        unlinkBtn.addEventListener('click', async () => {
            unlinkBtn.disabled = true;
            try {
                const result = await apiPatch(`${linkPath}/${linked.id}/block`, { block_id: null });
                if (result?.id !== undefined) {
                    showSuccessNotification('Сторінку відв\'язано');
                    onLinked({ id: result.id, slug: null });
                    close();
                } else {
                    showErrorNotification('Помилка при відв\'язці');
                    unlinkBtn.disabled = false;
                }
            } catch {
                showErrorNotification('Помилка при відв\'язці');
                unlinkBtn.disabled = false;
            }
        });
    }

    saveBtn.addEventListener('click', async () => {
        if (!selectedId) return;

        if (pendingMode) {
            onLinked({ id: selectedId, slug: selectedSlug });
            showSuccessNotification('Прив\'язку буде застосовано після публікації змін');
            close();
            return;
        }

        saveBtn.disabled = true;
        saveBtn.textContent = 'Зберігається...';
        try {
            const result = await apiPatch(
                `${linkPath}/${selectedId}/block`,
                { block_id: parseInt(blockId, 10) }
            );
            if (result?.id !== undefined) {
                showSuccessNotification(`Сторінку "${result.title}" прив'язано до картки`);
                onLinked(result);
                close();
            } else {
                showErrorNotification(result?.detail || 'Помилка при прив\'язці');
                saveBtn.disabled = false;
                saveBtn.textContent = 'Прив\'язати';
            }
        } catch (err) {
            showErrorNotification('Помилка при прив\'язці');
            saveBtn.disabled = false;
            saveBtn.textContent = 'Прив\'язати';
        }
    });
}
